import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { trackEvent } from "@koaform/analytics";
import {
  emptyFormDoc,
  instantiateTemplate,
  isLosslessTypeChange,
  newFieldId,
  templateBySlug,
  type FieldType,
  type FormDoc,
  type FormField,
} from "@koaform/form-schema";
import {
  fontStack,
  FormMedia,
  Logo,
  resolveThemeUrl,
  themeVars,
} from "@koaform/ui";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDraft } from "../hooks/useDraft.js";
import { usePageTitle } from "../hooks/usePageTitle.js";
import { api, ApiError, type Draft } from "../lib/api.js";
import { Share } from "../share/Share.js";
import { FIELD_GROUPS, makeField, type PaletteItem } from "./fieldDefaults.js";
import { CANVAS_APPEND_ID, DropHint, FieldList } from "./FieldList.js";
import { FieldSettings } from "./FieldSettings.js";
import { FormDesign } from "./FormDesign.js";
import { PreviewDialog } from "./PreviewDialog.js";
import { PublishDialog } from "./PublishDialog.js";
import { SaveIndicator } from "./SaveIndicator.js";

const RENDERER_URL =
  import.meta.env.VITE_RENDERER_URL ?? "http://localhost:5052";
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

/** Title inputs stay borderless until hovered — the design's quiet-edit pattern. */
const quietInput =
  "rounded-lg border border-transparent bg-transparent px-2.5 py-1.5 hover:border-line-strong focus:border-primary-600 focus:bg-subtle focus:outline-none";

/**
 * Seeds the draft from ?template=slug, so a "Use this template" link on the
 * marketing site opens a real form rather than an empty one.
 *
 * instantiateTemplate mints fresh field ids, so two forms from one template
 * never share an id. An unknown slug falls back to a blank form instead of
 * erroring — a stale bookmark should not be a dead end.
 */
const startingDoc = (): FormDoc => {
  const slug = new URLSearchParams(window.location.search).get("template");
  const template = slug ? templateBySlug(slug) : undefined;
  return template ? instantiateTemplate(template) : emptyFormDoc();
};

/** The chip a palette block shows — both at rest and while being dragged. */
const PaletteChip = ({ item }: { item: PaletteItem }) => (
  <>
    <span className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] bg-primary-100 font-mono text-[12px] text-primary-600">
      {item.glyph}
    </span>
    <span className="text-[13.5px] font-semibold text-body">{item.label}</span>
  </>
);

/**
 * A palette block: click to append, or drag it onto the canvas to place it.
 *
 * The KeyboardSensor's Enter/Space handler is stripped so a keyboard user's
 * Enter stays a plain click-to-add rather than starting a drag.
 */
const PaletteBlock = ({
  item,
  onAdd,
}: {
  item: PaletteItem;
  onAdd: () => void;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${item.type}`,
    data: { palette: item },
  });

  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onKeyDown={undefined}
      type="button"
      onClick={onAdd}
      title={`${item.hint} — click, or drag onto the form`}
      className={`flex w-full cursor-grab items-center gap-[11px] rounded-[10px] border border-line-strong bg-surface px-3 py-2.5 text-left hover:border-primary-600 hover:bg-primary-50 active:cursor-grabbing ${
        isDragging ? "opacity-40" : ""
      }`}
    >
      <PaletteChip item={item} />
    </button>
  );
};

/** The grouped palette — the desktop sidebar and the mobile sheet share it. */
const PaletteGroups = ({ onAdd }: { onAdd: (type: FieldType) => void }) => (
  <div className="flex flex-col gap-5">
    {FIELD_GROUPS.map((group) => (
      <div key={group.group}>
        <p className="mb-1.5 px-1 text-[11px] font-semibold text-faint">
          {group.group}
        </p>
        <div className="flex flex-col gap-1.5">
          {group.items.map((item) => (
            <PaletteBlock
              key={item.type}
              item={item}
              onAdd={() => onAdd(item.type)}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * The mobile stand-in for a sidebar: a bottom sheet.
 *
 * Below lg both sidebars are display:none, which used to mean phones could
 * not add a block or open any settings at all.
 */
const MobileSheet = ({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className="animate-fade absolute inset-0 w-full bg-ink/45"
    />
    <div className="animate-rise absolute inset-x-0 bottom-0 max-h-[78dvh] overflow-auto rounded-t-[20px] bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-20px_60px_-20px_rgba(12,26,46,0.45)]">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-5 py-3">
        <span className="font-mono text-[10px] tracking-[0.1em] text-faint">
          {title}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-3 py-1.5 text-sm font-bold text-primary-600"
        >
          Done
        </button>
      </div>
      {children}
    </div>
  </div>
);

export const Builder = ({
  existing,
  wipDoc,
}: {
  existing?: Draft;
  /** Unsaved edits mirrored to localStorage before the last refresh. */
  wipDoc?: FormDoc;
}) => {
  // useState's initialiser, so this runs once rather than on every render.
  // A dashboard Edit passes the saved form; otherwise seed new or template.
  const [initial] = useState(
    () => (existing ? undefined : wipDoc) ?? existing?.schema ?? startingDoc(),
  );
  const {
    doc,
    update,
    draftId,
    save,
    resumeToken,
    resolveConflict,
    ensureSaved,
  } = useDraft(initial, existing);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [slug, setSlug] = useState<string | null>(existing?.slug ?? null);
  const [dragging, setDragging] = useState<PaletteItem | null>(null);
  const [sheet, setSheet] = useState<"add" | "form" | null>(null);
  const [designOpen, setDesignOpen] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  // The private label. Held locally and pushed on blur rather than on every
  // keystroke — it is not document content, so it stays out of autosave.
  const [name, setName] = useState<string | null>(existing?.name ?? null);
  /**
   * Whether the draft differs from the live version.
   *
   * Seeded from the server, then tracked locally: the moment you edit a
   * published form there ARE unpublished changes, and waiting for the next
   * autosave round-trip to admit it would be the same silence this exists
   * to remove.
   */
  const [unpublished, setUnpublished] = useState(
    existing?.hasUnpublishedChanges ?? false,
  );
  const [revertOpen, setRevertOpen] = useState(false);
  const navigate = useNavigate();

  /** Every document edit goes through here, so the flag cannot drift. */
  const edit = (next: FormDoc) => {
    if (slug) setUnpublished(true);
    update(next);
  };

  const setFields = (fields: FormField[]) => edit({ ...doc, fields });
  const selected = doc.fields.find((f) => f.id === selectedId) ?? null;
  usePageTitle(doc.title.trim() || "New form");

  /**
   * A refresh mid-debounce leaves the newest edits only in the localStorage
   * mirror. Replaying them through update() puts them on screen AND schedules
   * the save the refresh interrupted. Mount-only: the mirror is by definition
   * newer than the server copy, or it would have been cleared.
   */
  useEffect(() => {
    if (existing && wipDoc) update(wipDoc);
    // Mount-only on purpose — replaying the mirror once is the whole point.
  }, []);

  /**
   * Write the draft id into the URL the moment it exists, so refreshing /new
   * resumes this very draft instead of a blank editor.
   */
  useEffect(() => {
    if (!draftId) return;
    const url = new URL(window.location.href);
    if (url.pathname === "/new" && url.searchParams.get("draft") !== draftId) {
      url.searchParams.set("draft", draftId);
      window.history.replaceState(null, "", url);
    }
  }, [draftId]);

  const sensors = useSensors(
    // 4px before a press becomes a drag, so palette clicks and card clicks
    // keep working.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    const palette = active.data.current?.palette as PaletteItem | undefined;
    setDragging(palette ?? null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const palette = dragging;
    setDragging(null);

    if (palette) {
      // A new block from the sidebar: insert before the card it was dropped
      // on, or append when dropped on the hint / empty canvas.
      if (!over) return;
      const field = makeField(palette.type);
      const at =
        over.id === CANVAS_APPEND_ID
          ? doc.fields.length
          : doc.fields.findIndex((f) => f.id === over.id);
      const next = [...doc.fields];
      next.splice(at === -1 ? doc.fields.length : at, 0, field);
      setFields(next);
      setSelectedId(field.id);
      return;
    }

    if (!over || active.id === over.id) return;
    const from = doc.fields.findIndex((f) => f.id === active.id);
    const to = doc.fields.findIndex((f) => f.id === over.id);
    if (from === -1 || to === -1) return;
    // Order is array position — responses key off field id, so reordering
    // can never affect them.
    setFields(arrayMove(doc.fields, from, to));
  };

  /**
   * Publishing is the signup gate. An anonymous draft gets a 401, which leaves
   * the dialog on its sign-up step; afterwards the claim moves this exact
   * draft into the new account with nothing retyped.
   */
  const publish = async (): Promise<string | null> => {
    setPublishing(true);
    setPublishOpen(true);
    setPublishError(null);
    try {
      // Flush first. Pressing Publish a moment after typing must not act on a
      // draft the server has never seen — and a form seeded from a template
      // has had no keystroke to trigger autosave at all.
      const id = await ensureSaved();
      if (!id) return null;

      const result = await api.publish(id);
      setSlug(result.slug);
      setUnpublished(false);
      // Counts only, never anything typed into the form — props are visible
      // in the Plausible UI.
      trackEvent("Form published", { fields: doc.fields.length });
      return result.slug;
    } catch (err) {
      // 401 is the signup gate, not an error: leave the dialog on its sign-up
      // step. Anything else is a real failure and the dialog should close —
      // but it must say WHY, or "a blank option blocks publishing" reads as
      // the button doing nothing at all.
      if (err instanceof ApiError && err.status === 401) return null;
      setPublishOpen(false);
      setPublishError(
        err instanceof ApiError
          ? err.message
          : "Could not publish that form. Try again.",
      );
      return null;
    } finally {
      setPublishing(false);
    }
  };

  /** Pushes the private name. Needs a saved draft, so it flushes first. */
  const saveName = async () => {
    const id = await ensureSaved();
    if (!id) return;
    try {
      await api.renameDraft(id, (name ?? "").trim());
    } catch {
      // A failed rename is cosmetic — the form and its content are untouched.
    }
  };

  /** Puts the draft back to exactly what respondents are seeing. */
  const discardChanges = async () => {
    if (!draftId) return;
    if (
      !window.confirm(
        "Discard every change made since you last published?\n\nThe form goes back to the version people are filling in now.",
      )
    ) {
      return;
    }
    try {
      const reverted = await api.revertDraft(draftId);
      // A full reload is the honest move: the draft's revision advanced
      // server-side, and this tab's in-memory copy is now the stale one.
      window.location.reload();
      return reverted;
    } catch (err) {
      setPublishError(
        err instanceof ApiError ? err.message : "Could not discard changes.",
      );
    }
  };

  const addField = (type: FieldType) => {
    const field = makeField(type);
    setFields([...doc.fields, field]);
    setSelectedId(field.id);
  };

  const duplicateField = (id: string) => {
    const index = doc.fields.findIndex((f) => f.id === id);
    const original = doc.fields[index];
    if (!original) return;
    // A copy is a NEW field: a shared id would collide in every response.
    const copy = { ...original, id: newFieldId() } as FormField;
    const next = [...doc.fields];
    next.splice(index + 1, 0, copy);
    setFields(next);
    setSelectedId(copy.id);
  };

  const changeField = (updated: FormField) => {
    const previous = doc.fields.find((f) => f.id === updated.id);
    // A type change that cannot carry existing answers mints a new field id,
    // leaving the old one to become a `removed` column. Without this a stored
    // answer stops matching its field's declared type.
    const needsNewId =
      previous && !isLosslessTypeChange(previous.type, updated.type);
    const next = needsNewId
      ? ({ ...updated, id: newFieldId() } as FormField)
      : updated;

    setFields(doc.fields.map((f) => (f.id === updated.id ? next : f)));
    if (needsNewId) setSelectedId(next.id);
  };

  const deleteField = (id: string) => {
    setFields(doc.fields.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <div className="relative flex h-dvh flex-col bg-paper">
      <header className="flex h-[60px] flex-none items-center justify-between gap-3 border-b border-line-strong bg-surface px-4 sm:gap-5 sm:px-5">
        <div className="flex min-w-0 items-center gap-3.5">
          {/* Full wordmark, not the bare mark — the brand stays visible on
              every screen, and the editor is where people spend the longest. */}
          <a href="/" aria-label="koaform" className="flex-none">
            <Logo size={26} />
          </a>
          {existing ? (
            <a
              href="/"
              className="hidden flex-none text-[13px] font-semibold text-muted hover:text-ink md:block"
            >
              ← My forms
            </a>
          ) : null}
          {/*
            The header edits the form's NAME — your private label for it,
            used in the dashboard. The big heading on the canvas is the
            public title respondents read. They were the same input before,
            so renaming a form for your own filing changed what strangers
            saw. Empty name falls back to the title.
          */}
          <span className="hidden h-6 w-px flex-none bg-line-strong md:block" />
          <input
            aria-label="Form name"
            title="Your private name for this form — respondents never see it"
            placeholder={doc.title || "Untitled form"}
            className={`hidden w-60 text-[16px] font-bold tracking-[-0.02em] text-ink md:block ${quietInput}`}
            value={name ?? ""}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void saveName()}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          />
          <span className="hidden font-mono text-[11px] text-faint xl:inline">
            Draft · saved in this browser
          </span>
          <SaveIndicator state={save} />

          {/*
            Editing a published form changes the DRAFT — the live form only
            moves when you publish again. Without this the editor looked like
            it was updating the live form and silently was not.
          */}
          {unpublished ? (
            <div className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setRevertOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-[12.5px] font-semibold text-amber-800 hover:bg-amber-200"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Unpublished changes
              </button>

              {revertOpen ? (
                <div className="animate-rise absolute top-9 left-0 z-30 w-[280px] rounded-[12px] border border-line-strong bg-surface p-4 shadow-[0_18px_44px_-16px_rgba(12,26,46,0.35)]">
                  <p className="m-0 mb-1 text-[13.5px] font-semibold text-ink">
                    Saved, but not live yet
                  </p>
                  <p className="m-0 mb-3.5 text-[12.5px] leading-[1.5] text-muted">
                    Anyone opening your link still sees the last published
                    version. Publish to make these changes public.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRevertOpen(false);
                        void publish();
                      }}
                      className="flex-1 rounded-[9px] bg-primary-600 px-3 py-2 text-[13px] font-bold text-white hover:bg-primary-700"
                    >
                      Publish now
                    </button>
                    <button
                      type="button"
                      onClick={() => void discardChanges()}
                      className="rounded-[9px] border border-line-bold bg-surface px-3 py-2 text-[13px] font-semibold text-danger hover:border-danger"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-none items-center gap-2 sm:gap-2.5">
          {slug ? (
            <button
              type="button"
              onClick={() => navigate(`/forms/${draftId}/responses`)}
              className="hidden rounded-[9px] border border-line-bold bg-surface px-4 py-[9px] text-sm font-semibold text-ink hover:border-primary-600 sm:block"
            >
              Responses
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              // Design lives behind this button: deselect so the panel shows.
              setSelectedId(null);
              setDesignOpen((open) => !open);
            }}
            title="Design & settings"
            aria-label="Design & settings"
            aria-pressed={designOpen && !selected}
            className={`hidden h-[38px] w-[38px] place-items-center rounded-[9px] border lg:grid ${
              designOpen && !selected
                ? "border-primary-600 bg-primary-50 text-primary-600"
                : "border-line-bold bg-surface text-muted hover:border-primary-600 hover:text-ink"
            }`}
          >
            <Cog6ToothIcon className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={doc.fields.length === 0}
            className="rounded-[9px] border border-line-bold bg-surface px-3 py-[9px] text-sm font-semibold text-ink hover:border-primary-600 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={() => void publish()}
            disabled={doc.fields.length === 0 || publishing}
            className="rounded-[9px] bg-primary-600 px-[18px] py-2.5 text-sm font-bold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-line-bold"
          >
            {publishing
              ? "Publishing…"
              : unpublished
                ? "Publish changes"
                : "Publish"}
          </button>
        </div>
      </header>

      {publishError ? (
        <div
          role="alert"
          className="border-b border-danger/25 bg-danger/5 px-5 py-3"
        >
          <div className="mx-auto flex max-w-[1120px] items-center gap-3 text-sm text-danger">
            <span className="flex-1">{publishError}</span>
            <button
              type="button"
              onClick={() => setPublishError(null)}
              aria-label="Dismiss"
              className="flex-none rounded-lg px-2 py-1 font-semibold hover:bg-danger/10"
            >
              ×
            </button>
          </div>
        </div>
      ) : null}

      {save.status === "conflict" ? (
        <div
          role="alert"
          className="border-b border-amber-200 bg-amber-50 px-5 py-3"
        >
          <div className="mx-auto flex max-w-[1120px] items-center gap-3 text-sm text-amber-900">
            <span className="flex-1">
              This form was edited in another tab. Your changes here have not
              been saved.
            </span>
            <button
              type="button"
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700"
              onClick={() => resolveConflict(save.server, save.revision)}
            >
              Load the other version
            </button>
          </div>
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        // A palette drag only counts when the pointer is over the canvas —
        // closestCenter would insert a field on a drop inside the sidebar.
        collisionDetection={dragging ? pointerWithin : closestCenter}
        modifiers={dragging ? [] : [restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDragging(null)}
      >
        <div className="grid min-h-0 flex-1 lg:grid-cols-[236px_1fr_300px]">
          {/* Palette */}
          <aside className="hidden overflow-auto border-r border-line-strong bg-surface px-4 py-5 lg:block">
            <h2 className="mb-3 font-mono text-[10px] tracking-[0.1em] text-faint">
              QUESTION BLOCKS
            </h2>
            <PaletteGroups onAdd={addField} />

            {resumeToken ? (
              <div className="mt-6 rounded-[10px] border border-dashed border-line-bold bg-subtle p-3.5">
                <p className="text-[12.5px] font-semibold text-body">
                  Save your progress
                </p>
                <p className="mt-1 text-[12.5px] leading-[1.5] text-faint">
                  No account yet — keep this link to come back to this form.
                </p>
                <input
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  value={`${RENDERER_URL}/d/${resumeToken}`}
                  className="mt-2 w-full rounded-lg border border-line-input bg-surface px-2 py-1.5 font-mono text-[11px] text-body"
                />
              </div>
            ) : null}
          </aside>

          {/* Canvas — themed live, so a color or font pick is visible the
              instant it lands rather than only inside Preview. */}
          <main
            className="overflow-auto px-4 pt-9 pb-32 lg:pb-20"
            onClick={() => setSelectedId(null)}
            style={{
              ...themeVars(doc.settings.theme),
              backgroundColor: doc.settings.theme.background ?? undefined,
              fontFamily: fontStack(doc.settings.theme.font),
            }}
          >
            <div className="mx-auto max-w-[620px]">
              <FormMedia
                className="mb-5"
                bannerUrl={resolveThemeUrl(
                  doc.settings.theme.bannerUrl,
                  API_URL,
                )}
                logoUrl={resolveThemeUrl(doc.settings.theme.logoUrl, API_URL)}
              />
              <input
                aria-label="Form title"
                className={`w-full text-[32px] font-extrabold tracking-[-0.035em] text-ink focus:bg-surface ${quietInput}`}
                value={doc.title}
                onChange={(e) => edit({ ...doc, title: e.target.value })}
              />
              <input
                aria-label="Form description"
                placeholder="Add a short description"
                className={`mb-5 w-full text-[16px] text-muted focus:bg-surface ${quietInput}`}
                value={doc.description ?? ""}
                onChange={(e) =>
                  edit({ ...doc, description: e.target.value || undefined })
                }
              />

              <FieldList
                fields={doc.fields}
                selectedId={selectedId}
                inserting={dragging !== null}
                onSelect={setSelectedId}
                onChange={changeField}
                onDelete={deleteField}
                onDuplicate={duplicateField}
              />

              {doc.fields.length > 0 ? (
                <>
                  <div className="mt-4">
                    <DropHint inserting={dragging !== null}>
                      <span className="lg:hidden">Tap “+ Add block” below</span>
                      <span className="hidden lg:inline">
                        Pick a block on the left — or drag one here
                      </span>
                    </DropHint>
                  </div>

                  {/*
                    The submit button sits where it will on the real form:
                    last. Clicking it opens the design panel, since its
                    label and colour are the only things about it to edit.
                  */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedId(null);
                      setDesignOpen(true);
                      setSheet("form");
                    }}
                    title="Edit the button’s label and colour"
                    className="group mt-5 flex items-center gap-3 px-5"
                  >
                    <span className="rounded-xl bg-primary-600 px-8 py-3.5 text-[16px] font-bold text-white shadow-[0_8px_20px_-8px_var(--color-primary-600)]">
                      {doc.settings.submitText}
                    </span>
                    <span className="text-[12.5px] text-faint opacity-0 transition-opacity group-hover:opacity-100">
                      Edit label &amp; colour
                    </span>
                  </button>
                </>
              ) : null}

              {slug ? (
                <div className="mt-8">
                  <Share slug={slug} />
                </div>
              ) : null}
            </div>
          </main>

          {/* Settings: the selected block's; form design only when asked for
              via the header's Design button. */}
          <aside className="hidden overflow-auto border-l border-line-strong bg-surface lg:block">
            {selected ? (
              <FieldSettings field={selected} onChange={changeField} />
            ) : designOpen ? (
              <FormDesign
                doc={doc}
                onChange={edit}
                ensureFormId={ensureSaved}
                published={slug !== null}
              />
            ) : (
              <p className="px-6 py-10 text-center text-sm leading-[1.55] text-faint">
                Select a question to edit it — or open{" "}
                <button
                  type="button"
                  onClick={() => setDesignOpen(true)}
                  className="font-semibold text-primary-600"
                >
                  Design
                </button>{" "}
                for colors, fonts and settings.
              </p>
            )}
          </aside>
        </div>

        {/* The block travelling under the cursor during a palette drag. */}
        <DragOverlay dropAnimation={null}>
          {dragging ? (
            <div className="flex w-[204px] items-center gap-[11px] rounded-[10px] border border-primary-600 bg-surface px-3 py-2.5 shadow-lg">
              <PaletteChip item={dragging} />
            </div>
          ) : null}
        </DragOverlay>

        {/* Mobile: both sidebars are hidden below lg, so a bottom bar stands
            in — add blocks and open settings from sheets instead. */}
        <div className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-line-strong bg-surface px-4 py-3 pb-[calc(12px+env(safe-area-inset-bottom))] lg:hidden">
          <button
            type="button"
            onClick={() => setSheet("add")}
            className="flex-1 rounded-[11px] bg-primary-600 py-3 text-[15px] font-bold text-white"
          >
            + Add block
          </button>
          <button
            type="button"
            onClick={() => setSheet("form")}
            className="flex-1 rounded-[11px] border border-line-bold bg-surface py-3 text-[15px] font-semibold text-ink"
          >
            {selected ? "Block settings" : "Design & settings"}
          </button>
        </div>

        {sheet === "add" ? (
          <MobileSheet title="ADD A BLOCK" onClose={() => setSheet(null)}>
            <div className="px-4 py-4">
              <PaletteGroups
                onAdd={(type) => {
                  addField(type);
                  setSheet(null);
                }}
              />
            </div>
          </MobileSheet>
        ) : null}

        {sheet === "form" ? (
          <MobileSheet
            title={selected ? "BLOCK SETTINGS" : "FORM SETTINGS"}
            onClose={() => setSheet(null)}
          >
            {selected ? (
              <FieldSettings field={selected} onChange={changeField} />
            ) : (
              <FormDesign
                doc={doc}
                onChange={edit}
                ensureFormId={ensureSaved}
                published={slug !== null}
              />
            )}
          </MobileSheet>
        ) : null}
      </DndContext>

      {previewOpen ? (
        <PreviewDialog doc={doc} onClose={() => setPreviewOpen(false)} />
      ) : null}

      {publishOpen ? (
        <PublishDialog
          title={doc.title}
          slug={slug}
          rendererUrl={RENDERER_URL}
          onPublish={publish}
          onClose={() => setPublishOpen(false)}
        />
      ) : null}
    </div>
  );
};

export type { FormDoc };
